import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ErrorBoundary } from 'shared-ui';

import { fetchObecnoscPosiadacze } from 'api/strefy/strefy';
import { ObecnoscPosiadaczeSortByEnum } from 'api/strefy/strefy.types';
import { QueryKeyEnum } from 'core/global.enum';
import { QueryKeyLocalStorageEnum } from 'context/query/queryContext/QueryContext.enum';
import { useQuery } from 'hooks/useQuery/useQuery';
import { useSavedPagination } from 'hooks/useSavedPagination/useSavedPagination';
import { PresenceLocationState, PresenceRefetchInterval } from '../CurrentPresence.types';
import { toPresenceRefetchInterval } from '../CurrentPresence.variables';

import { CurrentPresenceOwners } from './CurrentPresenceOwners';

export const CurrentPresenceOwnersContainer = () => {
  const { id = '' } = useParams<{ id: string }>();
  const { state } = useLocation();
  const [refetchInterval, setRefetchInterval] = useState<PresenceRefetchInterval>(() =>
    toPresenceRefetchInterval((state as PresenceLocationState | null)?.refetchInterval),
  );

  const { page, rowsPerPage, setPage, setRowsPerPage, storedQueryWithoutPagination, setPaginationCount } =
    useSavedPagination({
      queryKey: QueryKeyLocalStorageEnum.CURRENT_PRESENCE_OWNERS,
    });

  // The page lives in localStorage under one key for every zone; each zone's list starts on page 1.
  // `resetPaginationDependencyArray` would only cover a change while mounted, not entering from the tree.
  useEffect(() => {
    if (page !== 0) setPage(0);
  }, [id]);

  // The app stores sorting as `nazwisko` / `-nazwisko`; the endpoint takes `sortBy` + `sortOrder`.
  const [sort] = storedQueryWithoutPagination.sort ?? [];
  const komorka = storedQueryWithoutPagination.komorka?.length ? storedQueryWithoutPagination.komorka : undefined;

  const { data: ownersResponse, isLoading } = useQuery(
    [QueryKeyEnum.OBECNOSC_POSIADACZE, id, storedQueryWithoutPagination, page, rowsPerPage],
    () => fetchObecnoscPosiadacze(id),
    {
      args: {
        limit: rowsPerPage,
        offset: rowsPerPage * page,
        ...(sort && {
          sortBy: sort.replace(/^-/, '') as ObecnoscPosiadaczeSortByEnum,
          sortOrder: sort.startsWith('-') ? 'DESC' : 'ASC',
        }),
        ...(komorka && { komorka }),
      },
      refetchInterval,
      onSuccess: (response) => {
        setPaginationCount(response.count);
      },
    },
  );

  return (
    <ErrorBoundary>
      <CurrentPresenceOwners
        ownersResponse={ownersResponse}
        isLoading={isLoading}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        refetchInterval={refetchInterval}
        setRefetchInterval={setRefetchInterval}
      />
    </ErrorBoundary>
  );
};
